import { QuickSettings } from "@/components/QuickSettings";
import { SpotterButton } from "./SpotterButton";
import { SimpleButton } from "./SimpleButton";
import { SearchButton } from "./SearchButton";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { SvgIcon } from "@/svg";
import { create } from "zustand";
import { ChatButton } from "./ChatButton";
import { ChatTourProvider } from "./ChatTourProvider";
import { AIDashboardsButton } from "./AIDashboardsButton";
import { AISchedulesButton } from "./AISchedulesButton";
import { getCalendarDaysRemaining } from "@norma-bi/utils";
import { DASHBOARDS_PATH } from "@/constants";

export type HeaderElement = {
  enabled: boolean;
  isActive: boolean;
};

export type HeaderProps = {
  isDashboardsPath: boolean;
  searchData: HeaderElement;
  spotter: HeaderElement;
  chat: HeaderElement;
  aiDashboards: HeaderElement;
  aiSchedules: HeaderElement;
  onBusinessChange?: (id: string) => Promise<void>;
};

export function Header({
  isDashboardsPath,
  searchData,
  spotter,
  chat,
  aiDashboards,
  aiSchedules,
  onBusinessChange,
}: HeaderProps) {
  const businessContext = useBusinessContext();
  const businesses = useGetBusinesses();
  const expiresOn = businesses.data?.find((b) => b.id === businessContext.business?.id)?.licensing
    .web_app.expires_on;
  const daysRemaining = expiresOn ? getCalendarDaysRemaining(expiresOn) : undefined;

  const { show: showWarning, dismiss } = useTrialWarningStore();

  return (
    <ChatTourProvider chatEnabled={chat.enabled}>
      <div className="shrink-0 mt-2">
        <div className="flex justify-between items-center h-12 ml-4 mr-[1.2rem] bg-pureWhite rounded-lg text-sm">
          <div className="flex gap-6 px-6">
            {chat.enabled && <ChatButton isActive={chat.isActive} />}
            {aiDashboards.enabled && <AIDashboardsButton isActive={aiDashboards.isActive} />}
            {aiSchedules.enabled && <AISchedulesButton isActive={aiSchedules.isActive} />}
            <SimpleButton
              icon="HomeAlt"
              label="Dashboards"
              isActive={isDashboardsPath}
              href={DASHBOARDS_PATH}
              disabled={isDashboardsPath}
            />
            {searchData.enabled && <SearchButton isActive={searchData.isActive} />}
            {spotter.enabled && <SpotterButton isActive={spotter.isActive} />}
          </div>
          <div id="quick-settings-button" className="px-6 overflow-hidden">
            <QuickSettings
              hideArrows={true}
              hideBusinessName={false}
              onBusinessChange={onBusinessChange}
            />
          </div>
        </div>

        {showWarning && daysRemaining !== undefined && daysRemaining > 0 && (
          <div className="mx-4 mt-2 flex min-h-18 items-center gap-3 rounded-lg border border-[#F5C84B] bg-[#FFF9E8] px-4 py-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#FFF0B8] text-[#A87400]">
              <SvgIcon icon="Gift" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-base leading-5 text-dark">
                You have{" "}
                <strong className="font-semibold">
                  {daysRemaining} {daysRemaining === 1 ? "day" : "days"}
                </strong>{" "}
                of free AI credits remaining.
              </p>
              <p className="mt-0.5 text-xs text-feintGray">
                Contact your account manager to continue using Foodics AI.
              </p>
            </div>

            <button
              type="button"
              aria-label="Dismiss free AI credits warning"
              className="shrink-0 rounded-full p-1 text-[#6F5A26] hover:bg-[#FFF0B8]"
              onClick={dismiss}
            >
              <SvgIcon icon="Cross" />
            </button>
          </div>
        )}
      </div>
    </ChatTourProvider>
  );
}

type TrialWarningStore = {
  show: boolean;
  dismiss: () => void;
};

const useTrialWarningStore = create<TrialWarningStore>((set) => ({
  show: true,
  dismiss: () => set({ show: false }),
}));
